import { html, nothing } from "lit";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import type { ToolStreamEntry } from "../app-tool-stream.ts";
import {
  renderMessageGroup,
  renderReadingIndicatorGroup,
  renderStreamingGroup,
} from "../chat/grouped-render.ts";
import { extractTextCached } from "../chat/message-extract.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "../chat/message-normalizer.ts";
import { icons } from "../icons.ts";
import { detectTextDirection } from "../text-direction.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { ChatAttachment, ChatQueueItem } from "../ui-types.ts";
import { renderChatWelcome } from "./chat-welcome.ts";
import { renderMarkdownSidebar } from "./markdown-sidebar.ts";
import "../components/resizable-divider.ts";

export type CompactionIndicatorStatus = {
  active: boolean;
  startedAt: number | null;
  completedAt: number | null;
};

export type ChatProps = {
  sessionKey: string;
  onSessionKeyChange: (next: string) => void;
  thinkingLevel: string | null;
  showThinking: boolean;
  loading: boolean;
  sending: boolean;
  canAbort?: boolean;
  compactionStatus?: CompactionIndicatorStatus | null;
  messages: unknown[];
  toolMessages: unknown[];
  activityEntries?: ToolStreamEntry[];
  showToolMessagesInThread?: boolean;
  stream: string | null;
  streamStartedAt: number | null;
  assistantAvatarUrl?: string | null;
  draft: string;
  queue: ChatQueueItem[];
  connected: boolean;
  canSend: boolean;
  disabledReason: string | null;
  error: string | null;
  sessions: SessionsListResult | null;
  // Focus mode
  focusMode: boolean;
  // Sidebar state
  sidebarOpen?: boolean;
  sidebarContent?: string | null;
  sidebarError?: string | null;
  splitRatio?: number;
  assistantName: string;
  assistantAvatar: string | null;
  // Image attachments
  attachments?: ChatAttachment[];
  onAttachmentsChange?: (attachments: ChatAttachment[]) => void;
  // Scroll control
  showNewMessages?: boolean;
  onScrollToBottom?: () => void;
  // Event handlers
  onRefresh: () => void;
  onToggleFocusMode: () => void;
  onDraftChange: (next: string) => void;
  onSend: () => void;
  onAbort?: () => void;
  onQueueRemove: (id: string) => void;
  onNewSession: () => void;
  onToggleThinking?: () => void;
  onOpenSidebar?: (content: string) => void;
  onCloseSidebar?: () => void;
  onSplitRatioChange?: (ratio: number) => void;
  onChatScroll?: (event: Event) => void;
};

const COMPACTION_TOAST_DURATION_MS = 5000;

function adjustTextareaHeight(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function renderCompactionIndicator(status: CompactionIndicatorStatus | null | undefined) {
  if (!status) {
    return nothing;
  }

  // Show "compacting..." while active
  if (status.active) {
    return html`
      <div class="compaction-indicator compaction-indicator--active" role="status" aria-live="polite">
        ${icons.loader} Compacting context...
      </div>
    `;
  }

  // Show "compaction complete" briefly after completion
  if (status.completedAt) {
    const elapsed = Date.now() - status.completedAt;
    if (elapsed < COMPACTION_TOAST_DURATION_MS) {
      return html`
        <div class="compaction-indicator compaction-indicator--complete" role="status" aria-live="polite">
          ${icons.check} Context compacted
        </div>
      `;
    }
  }

  return nothing;
}

function formatActivityName(name: string): string {
  if (!name.trim()) {
    return "Agent step";
  }
  const normalized = name.replace(/\./g, " ").replace(/[_-]+/g, " ").trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function summarizeActivityOutput(output?: string): string {
  if (!output) {
    return "";
  }
  const compact = output.replace(/\s+/g, " ").trim();
  if (!compact) {
    return "";
  }
  const limit = 140;
  return compact.length > limit ? `${compact.slice(0, limit - 1)}...` : compact;
}

type OperationalStepState = "running" | "done" | "failed";

type OperationalStep = {
  name: string;
  detail: string;
  state: OperationalStepState;
};

type OperationalAnalysis = {
  isOperational: boolean;
  steps: OperationalStep[];
  hasFailure: boolean;
  completedTasks: number;
  browserActions: number;
  browserActionKinds: string[];
  hasInteractiveBrowserAction: boolean;
};

function formatOperationalActionList(actions: string[]): string {
  const limit = 4;
  if (actions.length <= limit) {
    return actions.join(", ");
  }
  return `${actions.slice(0, limit).join(", ")}, ...`;
}

function parseOperationalTranscript(text: string): OperationalAnalysis {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const steps: OperationalStep[] = [];
  let recognized = 0;
  let completedTasks = 0;
  let browserActions = 0;
  let hasFailure = false;
  const browserActionKinds: string[] = [];
  let hasInteractiveBrowserAction = false;

  for (const line of lines) {
    const taskMatch = line.match(/^Task\s+[^:]+:\s+(.+?)\s*->\s*(running|completed|failed)\s*$/i);
    if (taskMatch) {
      recognized += 1;
      const taskName = taskMatch[1]?.trim() || "Task";
      const statusRaw = taskMatch[2]?.toLowerCase();
      const state: OperationalStepState =
        statusRaw === "running" ? "running" : statusRaw === "failed" ? "failed" : "done";
      if (state === "done") {
        completedTasks += 1;
      }
      if (state === "failed") {
        hasFailure = true;
      }
      steps.push({
        name: taskName,
        detail: state === "running" ? "In progress" : state === "failed" ? "Failed" : "Completed",
        state,
      });
      continue;
    }

    const browserMatch = line.match(/^Browser action\s+([a-z0-9_.-]+)\s+(success|failed)\b/i);
    if (browserMatch) {
      recognized += 1;
      browserActions += 1;
      const actionName = browserMatch[1] || "action";
      const normalizedActionName = actionName.toLowerCase();
      if (normalizedActionName && !browserActionKinds.includes(normalizedActionName)) {
        browserActionKinds.push(normalizedActionName);
      }
      if (
        normalizedActionName &&
        normalizedActionName !== "navigate" &&
        normalizedActionName !== "screenshot" &&
        normalizedActionName !== "open_live_view"
      ) {
        hasInteractiveBrowserAction = true;
      }
      const ok = browserMatch[2]?.toLowerCase() === "success";
      if (!ok) {
        hasFailure = true;
      }
      steps.push({
        name: `Browser: ${actionName}`,
        detail: ok ? "Completed" : "Failed",
        state: ok ? "done" : "failed",
      });
      continue;
    }

    const missionMatch = line.match(/^Mission\s+[^\s]+\s+updated:\s*([a-z_]+)\s*$/i);
    if (missionMatch) {
      recognized += 1;
      const status = missionMatch[1]?.toLowerCase() || "";
      let state: OperationalStepState = "done";
      let detail = "Updated";
      if (status === "running" || status === "planned" || status === "draft") {
        state = "running";
        detail = status;
      } else if (status === "failed") {
        state = "failed";
        detail = status;
        hasFailure = true;
      } else if (status === "completed") {
        detail = status;
      } else {
        detail = status || "updated";
      }
      steps.push({ name: "Mission", detail, state });
      continue;
    }

    const missionAcceptedMatch = line.match(/^Mission accepted:\s+[^\s]+\s+\(([^)]+)\)\s*$/i);
    if (missionAcceptedMatch) {
      recognized += 1;
      const status = missionAcceptedMatch[1]?.toLowerCase() || "";
      const state: OperationalStepState = status === "failed" ? "failed" : "done";
      if (state === "failed") {
        hasFailure = true;
      }
      steps.push({
        name: "Mission accepted",
        detail: status || "completed",
        state,
      });
      continue;
    }

    if (
      /^Routed message to mission\b/i.test(line) ||
      /^executor\.[a-z0-9_.-]+\b/i.test(line) ||
      /^Incident\b/i.test(line)
    ) {
      recognized += 1;
      const isIncident = /^Incident\b/i.test(line);
      if (isIncident) {
        hasFailure = true;
      }
      steps.push({
        name: isIncident ? "Incident" : "Agent event",
        detail: line,
        state: isIncident ? "failed" : "done",
      });
    }
  }

  const isOperational =
    lines.length >= 3 && recognized >= 2 && recognized / Math.max(1, lines.length) >= 0.55;
  return {
    isOperational,
    steps: isOperational ? steps : [],
    hasFailure,
    completedTasks,
    browserActions,
    browserActionKinds,
    hasInteractiveBrowserAction,
  };
}

function buildOperationalSummary(text: string): string | null {
  const analysis = parseOperationalTranscript(text);
  if (!analysis.isOperational) {
    return null;
  }
  const actionsLabel =
    analysis.browserActionKinds.length > 0
      ? formatOperationalActionList(analysis.browserActionKinds)
      : null;
  if (analysis.hasFailure) {
    const parts: string[] = [];
    if (analysis.completedTasks > 0) {
      parts.push(
        `after ${analysis.completedTasks} completed step${analysis.completedTasks === 1 ? "" : "s"}`,
      );
    }
    const prefix =
      parts.length > 0
        ? `Run finished with issues ${parts.join(" ")}.`
        : "Run finished with issues.";
    return `${prefix}${actionsLabel ? ` Browser actions: ${actionsLabel}.` : ""} Check Reasoning steps for details.`;
  }
  if (analysis.browserActions > 0 && !analysis.hasInteractiveBrowserAction) {
    return `Run completed with basic browser steps${actionsLabel ? ` (${actionsLabel})` : ""}. No interactive actions were executed yet.`;
  }
  const parts: string[] = [];
  if (analysis.completedTasks > 0) {
    parts.push(`${analysis.completedTasks} step${analysis.completedTasks === 1 ? "" : "s"}`);
  }
  if (analysis.browserActions > 0) {
    parts.push(
      `${analysis.browserActions} browser action${analysis.browserActions === 1 ? "" : "s"}`,
    );
  }
  if (parts.length === 0) {
    return "Run completed. Check Reasoning steps for details.";
  }
  return `Done. Completed ${parts.join(" and ")}${actionsLabel ? ` (${actionsLabel})` : ""}.`;
}

function getMessageTimestamp(message: unknown): number {
  const m = message as Record<string, unknown>;
  return typeof m.timestamp === "number" ? m.timestamp : Date.now();
}

function collectOperationalStepsFromMessages(
  messages: unknown[],
  stream: string | null,
): OperationalStep[] {
  const collected: OperationalStep[] = [];
  for (const message of messages) {
    const text = extractTextCached(message);
    if (!text) {
      continue;
    }
    const parsed = parseOperationalTranscript(text);
    if (!parsed.isOperational) {
      continue;
    }
    collected.push(...parsed.steps);
  }
  if (stream) {
    const parsed = parseOperationalTranscript(stream);
    if (parsed.steps.length > 0) {
      collected.push(...parsed.steps);
    }
  }
  return collected.slice(-6);
}

type ActivityDisplayEntry = {
  name: string;
  detail: string;
  running: boolean;
  failed: boolean;
  recent: boolean;
};

function renderAgentActivity(props: ChatProps) {
  if (!props.showThinking) {
    return nothing;
  }

  const now = Date.now();
  const toolEntries = (props.activityEntries ?? []).slice(-6).toReversed();
  const toolDisplay: ActivityDisplayEntry[] = toolEntries.map((entry) => {
    const running = entry.status === "running";
    const failed = entry.status === "failed";
    const detail =
      summarizeActivityOutput(entry.output) ||
      (running ? "In progress" : failed ? "Failed" : "Completed");
    return {
      name: formatActivityName(entry.name),
      detail,
      running,
      failed,
      recent: !running && now - entry.updatedAt < 2500,
    };
  });

  const operationalDisplay: ActivityDisplayEntry[] = collectOperationalStepsFromMessages(
    props.messages,
    props.stream,
  )
    .toReversed()
    .map((entry) => ({
      name: entry.name,
      detail: entry.detail,
      running: entry.state === "running",
      failed: entry.state === "failed",
      recent: false,
    }));

  const entries = toolDisplay.length > 0 ? toolDisplay : operationalDisplay;
  const hasRunning = Boolean(props.stream) || entries.some((entry) => entry.running);
  if (entries.length === 0 && !hasRunning) {
    return nothing;
  }

  return html`
    <section class="agent-activity ${hasRunning ? "agent-activity--running" : ""}">
      <div class="agent-activity__header">
        <div class="agent-activity__title">
          <span class="agent-activity__title-icon">${hasRunning ? icons.loader : icons.brain}</span>
          <span>Reasoning steps</span>
        </div>
        <div class="agent-activity__meta">
          <span class="agent-activity__status">${hasRunning ? "Agent is working" : "Complete"}</span>
        </div>
      </div>
      <div class="agent-activity__list">
        ${entries.map((entry) => {
          const isRunning = entry.running;
          const detail = entry.detail;
          return html`
            <div
              class="agent-activity__item ${isRunning ? "agent-activity__item--running" : ""} ${entry.failed ? "agent-activity__item--failed" : ""} ${entry.recent ? "agent-activity__item--recent" : ""}"
            >
              <span class="agent-activity__dot ${isRunning ? "agent-activity__dot--pulse" : ""}"></span>
              <div class="agent-activity__content">
                <div class="agent-activity__row">
                  <span class="agent-activity__name">${entry.name}</span>
                  <span class="agent-activity__state">${isRunning ? "In progress" : entry.failed ? "Failed" : "Done"}</span>
                </div>
                ${detail ? html`<div class="agent-activity__detail">${detail}</div>` : nothing}
              </div>
            </div>
          `;
        })}
      </div>
    </section>
  `;
}

function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function handlePaste(e: ClipboardEvent, props: ChatProps) {
  const items = e.clipboardData?.items;
  if (!items || !props.onAttachmentsChange) {
    return;
  }

  const imageItems: DataTransferItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith("image/")) {
      imageItems.push(item);
    }
  }

  if (imageItems.length === 0) {
    return;
  }

  e.preventDefault();

  for (const item of imageItems) {
    const file = item.getAsFile();
    if (!file) {
      continue;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const dataUrl = reader.result as string;
      const newAttachment: ChatAttachment = {
        id: generateAttachmentId(),
        dataUrl,
        mimeType: file.type,
      };
      const current = props.attachments ?? [];
      props.onAttachmentsChange?.([...current, newAttachment]);
    });
    reader.readAsDataURL(file);
  }
}

function handleFileDrop(e: DragEvent, props: ChatProps) {
  const files = e.dataTransfer?.files;
  if (!files || !props.onAttachmentsChange) {
    return;
  }
  for (const file of Array.from(files)) {
    if (!file.type.startsWith("image/")) {
      continue;
    }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const newAttachment: ChatAttachment = {
        id: generateAttachmentId(),
        name: file.name,
        dataUrl: reader.result as string,
        mimeType: file.type,
      };
      const current = props.attachments ?? [];
      props.onAttachmentsChange?.([...current, newAttachment]);
    });
    reader.readAsDataURL(file);
  }
}

function handleFileSelect(e: Event, props: ChatProps) {
  const input = e.target as HTMLInputElement;
  const files = input.files;
  if (!files || !props.onAttachmentsChange) {
    return;
  }
  for (const file of Array.from(files)) {
    if (!file.type.startsWith("image/")) {
      continue;
    }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const newAttachment: ChatAttachment = {
        id: generateAttachmentId(),
        name: file.name,
        dataUrl: reader.result as string,
        mimeType: file.type,
      };
      const current = props.attachments ?? [];
      props.onAttachmentsChange?.([...current, newAttachment]);
    });
    reader.readAsDataURL(file);
  }
  // Reset input so the same file can be selected again
  input.value = "";
}

function renderAttachmentPreview(props: ChatProps) {
  const attachments = props.attachments ?? [];
  if (attachments.length === 0) {
    return nothing;
  }

  return html`
    <div class="chat-attachments">
      ${attachments.map(
        (att) => html`
          <div class="chat-attachment">
            <img
              src=${att.dataUrl}
              alt="Attachment preview"
              class="chat-attachment__img"
            />
            <button
              class="chat-attachment__remove"
              type="button"
              aria-label="Remove attachment"
              @click=${() => {
                const next = (props.attachments ?? []).filter((a) => a.id !== att.id);
                props.onAttachmentsChange?.(next);
              }}
            >
              ${icons.x}
            </button>
          </div>
        `,
      )}
    </div>
  `;
}

export function renderChat(props: ChatProps) {
  const canCompose = props.connected;
  const isBusy = props.sending || props.stream !== null;
  const canAbort = Boolean(props.canAbort && props.onAbort);
  const showReconnectNotice = Boolean(props.stream !== null && !props.connected);
  const activeSession = props.sessions?.sessions?.find((row) => row.key === props.sessionKey);
  const reasoningLevel = activeSession?.reasoningLevel ?? "off";
  const showReasoning = props.showThinking && reasoningLevel !== "off";
  const assistantIdentity = {
    name: props.assistantName,
    avatar: props.assistantAvatar ?? props.assistantAvatarUrl ?? null,
  };

  const hasAttachments = (props.attachments?.length ?? 0) > 0;
  const composePlaceholder = props.connected
    ? hasAttachments
      ? "Add a message or paste more images..."
      : "Message (↩ to send, Shift+↩ for line breaks, paste images)"
    : "Connect to the gateway to start chatting…";

  const splitRatio = props.splitRatio ?? 0.6;
  const sidebarOpen = Boolean(props.sidebarOpen && props.onCloseSidebar);

  // Check if chat is empty (no messages and not loading/streaming)
  const hasNoMessages =
    !props.loading && props.messages.length === 0 && !props.stream && props.queue.length === 0;

  const thread = hasNoMessages
    ? html`
        <div class="chat-thread">
          ${renderChatWelcome({
            connected: props.connected,
            onSelectSuggestion: (prompt: string) => {
              if (prompt) {
                props.onDraftChange(prompt);
              }
            },
          })}
        </div>
      `
    : html`
        <div
          class="chat-thread"
          role="log"
          aria-live="polite"
          @scroll=${props.onChatScroll}
        >
          ${
            props.loading
              ? html`
                  <div class="muted">Loading chat…</div>
                `
              : nothing
          }
          ${repeat(
            buildChatItems(props),
            (item) => item.key,
            (item) => {
              if (item.kind === "divider") {
                return html`
                  <div class="chat-divider" role="separator" data-ts=${String(item.timestamp)}>
                    <span class="chat-divider__line"></span>
                    <span class="chat-divider__label">${item.label}</span>
                    <span class="chat-divider__line"></span>
                  </div>
                `;
              }

              if (item.kind === "reading-indicator") {
                return renderReadingIndicatorGroup(assistantIdentity);
              }

              if (item.kind === "stream") {
                return renderStreamingGroup(
                  item.text,
                  item.startedAt,
                  props.onOpenSidebar,
                  assistantIdentity,
                );
              }

              if (item.kind === "group") {
                return renderMessageGroup(item, {
                  onOpenSidebar: props.onOpenSidebar,
                  showReasoning,
                  assistantName: props.assistantName,
                  assistantAvatar: assistantIdentity.avatar,
                });
              }

              return nothing;
            },
          )}
        </div>
      `;

  return html`
    <section class="card chat">
      ${props.disabledReason ? html`<div class="callout">${props.disabledReason}</div>` : nothing}

      ${
        showReconnectNotice
          ? html`
              <div class="callout info">
                ${icons.loader} Disconnected from gateway. Reconnecting...
              </div>
            `
          : nothing
      }

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        props.onToggleThinking
          ? html`
              <div class="chat-reasoning-toggle-wrap">
                <button
                  class="agent-activity__toggle"
                  type="button"
                  @click=${props.onToggleThinking}
                >
                  ${props.showThinking ? "Reasoning on" : "Reasoning off"}
                </button>
              </div>
            `
          : nothing
      }

      ${renderCompactionIndicator(props.compactionStatus)}
      ${renderAgentActivity(props)}

      ${
        props.focusMode
          ? html`
            <button
              class="chat-focus-exit"
              type="button"
              @click=${props.onToggleFocusMode}
              aria-label="Exit focus mode"
              title="Exit focus mode"
            >
              ${icons.x}
            </button>
          `
          : nothing
      }

      <div
        class="chat-split-container ${sidebarOpen ? "chat-split-container--open" : ""}"
      >
        <div
          class="chat-main"
          style="flex: ${sidebarOpen ? `0 0 ${splitRatio * 100}%` : "1 1 100%"}"
        >
          ${thread}
        </div>

        ${
          sidebarOpen
            ? html`
              <resizable-divider
                .splitRatio=${splitRatio}
                @resize=${(e: CustomEvent) => props.onSplitRatioChange?.(e.detail.splitRatio)}
              ></resizable-divider>
              <div class="chat-sidebar">
                ${renderMarkdownSidebar({
                  content: props.sidebarContent ?? null,
                  error: props.sidebarError ?? null,
                  onClose: props.onCloseSidebar!,
                  onViewRawText: () => {
                    if (!props.sidebarContent || !props.onOpenSidebar) {
                      return;
                    }
                    props.onOpenSidebar(`\`\`\`\n${props.sidebarContent}\n\`\`\``);
                  },
                })}
              </div>
            `
            : nothing
        }
      </div>

      ${
        props.queue.length
          ? html`
            <div class="chat-queue" role="status" aria-live="polite">
              <div class="chat-queue__title">Queued (${props.queue.length})</div>
              <div class="chat-queue__list">
                ${props.queue.map(
                  (item) => html`
                    <div class="chat-queue__item">
                      <div class="chat-queue__text">
                        ${
                          item.text ||
                          (item.attachments?.length ? `Image (${item.attachments.length})` : "")
                        }
                      </div>
                      <button
                        class="btn chat-queue__remove"
                        type="button"
                        aria-label="Remove queued message"
                        @click=${() => props.onQueueRemove(item.id)}
                      >
                        ${icons.x}
                      </button>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
          : nothing
      }

      ${
        props.showNewMessages
          ? html`
            <button
              class="btn chat-new-messages"
              type="button"
              @click=${props.onScrollToBottom}
            >
              New messages ${icons.arrowDown}
            </button>
          `
          : nothing
      }

      <div
        class="chat-compose"
        @dragover=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).classList.add("chat-compose--dragover");
        }}
        @dragleave=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).classList.remove("chat-compose--dragover");
        }}
        @drop=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).classList.remove("chat-compose--dragover");
          handleFileDrop(e, props);
        }}
      >
        ${renderAttachmentPreview(props)}
        <div class="chat-compose__row">
          <div class="chat-compose__toolbar">
            <input
              type="file"
              id="chat-attach-input"
              accept="image/*"
              multiple
              style="display: none"
              @change=${(e: Event) => handleFileSelect(e, props)}
            />
            <button
              class="btn btn--icon chat-compose__attach"
              title="Attach image"
              @click=${() => {
                const input = document.getElementById("chat-attach-input") as HTMLInputElement;
                input?.click();
              }}
            >
              ${icons.paperclip}
            </button>
          </div>
          <label class="field chat-compose__field">
            <span>Message</span>
            <textarea
              ${ref((el) => el && adjustTextareaHeight(el as HTMLTextAreaElement))}
              .value=${props.draft}
              dir=${detectTextDirection(props.draft)}
              ?disabled=${!props.connected}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key !== "Enter") {
                  return;
                }
                if (e.isComposing || e.keyCode === 229) {
                  return;
                }
                if (e.shiftKey) {
                  return;
                } // Allow Shift+Enter for line breaks
                if (!props.connected) {
                  return;
                }
                e.preventDefault();
                if (canCompose) {
                  props.onSend();
                }
              }}
              @input=${(e: Event) => {
                const target = e.target as HTMLTextAreaElement;
                adjustTextareaHeight(target);
                props.onDraftChange(target.value);
              }}
              @paste=${(e: ClipboardEvent) => handlePaste(e, props)}
              placeholder=${composePlaceholder}
            ></textarea>
          </label>
          <div class="chat-compose__actions">
            <button
              class="btn"
              ?disabled=${!props.connected || (!canAbort && props.sending)}
              @click=${canAbort ? props.onAbort : props.onNewSession}
            >
              ${canAbort ? "Stop" : "New session"}
            </button>
            <button
              class="btn primary"
              ?disabled=${!props.connected}
              @click=${props.onSend}
            >
              ${isBusy ? "Queue" : "Send"}<kbd class="btn-kbd">↵</kbd>
            </button>
          </div>
        </div>
      </div>
    </section>
  `;
}

const CHAT_HISTORY_RENDER_LIMIT = 200;

function groupMessages(items: ChatItem[]): Array<ChatItem | MessageGroup> {
  const result: Array<ChatItem | MessageGroup> = [];
  let currentGroup: MessageGroup | null = null;

  for (const item of items) {
    if (item.kind !== "message") {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(item);
      continue;
    }

    const normalized = normalizeMessage(item.message);
    const role = normalizeRoleForGrouping(normalized.role);
    const timestamp = normalized.timestamp || Date.now();

    if (!currentGroup || currentGroup.role !== role) {
      if (currentGroup) {
        result.push(currentGroup);
      }
      currentGroup = {
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      };
    } else {
      currentGroup.messages.push({ message: item.message, key: item.key });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }
  return result;
}

function buildChatItems(props: ChatProps): Array<ChatItem | MessageGroup> {
  const items: ChatItem[] = [];
  const history = Array.isArray(props.messages) ? props.messages : [];
  const tools = Array.isArray(props.toolMessages) ? props.toolMessages : [];
  const historyStart = Math.max(0, history.length - CHAT_HISTORY_RENDER_LIMIT);
  let pendingOperationalSummary: { summary: string; timestamp: number } | null = null;

  const flushPendingOperationalSummary = () => {
    if (!pendingOperationalSummary) {
      return;
    }
    items.push({
      kind: "message",
      key: `operational:${pendingOperationalSummary.timestamp}`,
      message: {
        role: "assistant",
        content: [{ type: "text", text: pendingOperationalSummary.summary }],
        timestamp: pendingOperationalSummary.timestamp,
      },
    });
    pendingOperationalSummary = null;
  };

  if (historyStart > 0) {
    items.push({
      kind: "message",
      key: "chat:history:notice",
      message: {
        role: "system",
        content: `Showing last ${CHAT_HISTORY_RENDER_LIMIT} messages (${historyStart} hidden).`,
        timestamp: Date.now(),
      },
    });
  }
  for (let i = historyStart; i < history.length; i++) {
    const msg = history[i];
    const normalized = normalizeMessage(msg);
    const raw = msg as Record<string, unknown>;
    const marker = raw.__openclaw as Record<string, unknown> | undefined;
    if (marker && marker.kind === "compaction") {
      items.push({
        kind: "divider",
        key:
          typeof marker.id === "string"
            ? `divider:compaction:${marker.id}`
            : `divider:compaction:${normalized.timestamp}:${i}`,
        label: "Compaction",
        timestamp: normalized.timestamp ?? Date.now(),
      });
      continue;
    }

    const extracted = extractTextCached(msg);
    const operationalSummary =
      normalized.role.toLowerCase() === "assistant" && extracted
        ? buildOperationalSummary(extracted)
        : null;

    if (operationalSummary) {
      pendingOperationalSummary = {
        summary: operationalSummary,
        timestamp: getMessageTimestamp(msg),
      };
      continue;
    }

    flushPendingOperationalSummary();

    if (!props.showThinking && normalized.role.toLowerCase() === "toolresult") {
      continue;
    }

    items.push({
      kind: "message",
      key: messageKey(msg, i),
      message: msg,
    });
  }
  flushPendingOperationalSummary();
  if (props.showThinking && props.showToolMessagesInThread) {
    for (let i = 0; i < tools.length; i++) {
      items.push({
        kind: "message",
        key: messageKey(tools[i], i + history.length),
        message: tools[i],
      });
    }
  }

  if (props.stream !== null) {
    const key = `stream:${props.sessionKey}:${props.streamStartedAt ?? "live"}`;
    const streamText = props.stream.trim();
    if (streamText.length > 0) {
      const operational = parseOperationalTranscript(streamText);
      if (operational.isOperational) {
        items.push({ kind: "reading-indicator", key });
        return groupMessages(items);
      }
      items.push({
        kind: "stream",
        key,
        text: props.stream,
        startedAt: props.streamStartedAt ?? Date.now(),
      });
    } else {
      items.push({ kind: "reading-indicator", key });
    }
  }

  return groupMessages(items);
}

function messageKey(message: unknown, index: number): string {
  const m = message as Record<string, unknown>;
  const toolCallId = typeof m.toolCallId === "string" ? m.toolCallId : "";
  if (toolCallId) {
    return `tool:${toolCallId}`;
  }
  const id = typeof m.id === "string" ? m.id : "";
  if (id) {
    return `msg:${id}`;
  }
  const messageId = typeof m.messageId === "string" ? m.messageId : "";
  if (messageId) {
    return `msg:${messageId}`;
  }
  const timestamp = typeof m.timestamp === "number" ? m.timestamp : null;
  const role = typeof m.role === "string" ? m.role : "unknown";
  if (timestamp != null) {
    return `msg:${role}:${timestamp}:${index}`;
  }
  return `msg:${role}:${index}`;
}
